
import React, { useState } from 'react';
import { UserRole, User } from './types';
import { MOCK_USERS } from './mockData';
import Sidebar from './components/Sidebar';
import Header from './components/Header';
import Dashboard from './components/Dashboard';
import Tickets from './components/Tickets';
import Announcements from './components/Announcements';
import Voting from './components/Voting';
import Calendar from './components/Calendar';
import Buildings from './components/Buildings';
import Residents from './components/Residents';
import Communication from './components/Communication';
import Finances from './components/Finances';
import { BellRing, ShieldCheck, User as UserIcon } from 'lucide-react';

const App: React.FC = () => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [activeTab, setActiveTab] = useState<string>('dashboard');
  const [targetRecipientId, setTargetRecipientId] = useState<string | null>(null);
  const [notification, setNotification] = useState<{ message: string; show: boolean }>({ message: '', show: false });

  const triggerNotification = (message: string) => {
    setNotification({ message, show: true });
    setTimeout(() => setNotification({ message: '', show: false }), 5000);
  };

  const navigateToMessage = (userId: string) => {
    setTargetRecipientId(userId);
    setActiveTab('communication');
  };

  const getRoleLabel = (role: UserRole) => {
    switch(role) {
      case UserRole.SUPER_ADMIN: return 'Супер Администратор';
      case UserRole.PRESIDENT: return 'Претседател';
      case UserRole.DEPUTY: return 'Заменик';
      case UserRole.TENANT: return 'Станар';
      case UserRole.MAINTENANCE_STAFF: return 'Одржување';
      default: return role;
    }
  };

  const renderContent = () => {
    if (!currentUser) return null;

    switch (activeTab) {
      case 'dashboard':
        return <Dashboard user={currentUser} onNavigate={setActiveTab} />;
      case 'tickets':
        return <Tickets user={currentUser} onStatusChange={triggerNotification} />;
      case 'announcements':
        return <Announcements user={currentUser} />;
      case 'voting':
        return <Voting user={currentUser} />;
      case 'calendar':
        return <Calendar user={currentUser} />;
      case 'buildings':
        return <Buildings user={currentUser} />;
      case 'residents':
        return <Residents user={currentUser} onMessageRequest={navigateToMessage} />;
      case 'finances':
        return <Finances user={currentUser} />;
      case 'communication':
        return <Communication user={currentUser} initialRecipientId={targetRecipientId} onMessageSent={() => setTargetRecipientId(null)} />;
      default:
        return <Dashboard user={currentUser} onNavigate={setActiveTab} />;
    }
  };

  if (!currentUser) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center p-6 bg-[url('https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?auto=format&fit=crop&q=80')] bg-cover bg-center">
        <div className="absolute inset-0 bg-slate-900/80 backdrop-blur-sm"></div>
        <div className="relative bg-white w-full max-w-md p-10 rounded-[2.5rem] shadow-2xl animate-in zoom-in duration-300">
           <div className="text-center mb-10">
              <div className="w-20 h-20 bg-indigo-600 rounded-[2rem] flex items-center justify-center mx-auto mb-6 shadow-xl shadow-indigo-200">
                 <ShieldCheck className="w-10 h-10 text-white" />
              </div>
              <h1 className="text-3xl font-black text-slate-900 tracking-tight">CivicHome</h1>
              <p className="text-slate-500 font-medium mt-2">Концепт за дигитално домување</p>
           </div>
           
           <div className="space-y-4">
              <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest text-center mb-4">Избери профил за преглед</p>
              {Object.entries(MOCK_USERS).map(([key, user]) => (
                <button 
                  key={key}
                  onClick={() => setCurrentUser(user)}
                  className="w-full flex items-center p-4 rounded-2xl border border-slate-100 hover:border-indigo-200 hover:bg-indigo-50 transition-all group"
                >
                  <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center text-slate-400 group-hover:bg-white group-hover:text-indigo-600 transition-colors mr-4">
                    <UserIcon className="w-5 h-5" />
                  </div>
                  <div className="text-left">
                    <p className="font-bold text-slate-900 leading-tight">{user.name}</p>
                    <p className="text-xs text-slate-400 font-bold uppercase tracking-tighter">{getRoleLabel(user.role)}</p>
                  </div>
                </button>
              ))}
           </div>
           
           <div className="mt-10 text-center">
              <p className="text-[10px] text-slate-300 font-medium">CivicHome в1.0 • Спецификација за 2025</p>
           </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-slate-50 relative overflow-hidden">
      {notification.show && (
        <div className="fixed top-4 right-4 z-[100] animate-in slide-in-from-right duration-300">
          <div className="bg-indigo-600 text-white px-6 py-4 rounded-2xl shadow-2xl flex items-center space-x-4 border border-indigo-400">
            <BellRing className="w-6 h-6 animate-bounce" />
            <div>
              <p className="font-bold text-sm uppercase tracking-wider">Ново известување</p>
              <p className="text-sm opacity-90">{notification.message}</p>
            </div>
          </div>
        </div>
      )}

      <Sidebar 
        activeTab={activeTab} 
        setActiveTab={setActiveTab} 
        userRole={currentUser.role} 
      />
      
      <div className="flex-1 flex flex-col min-w-0">
        <Header 
          user={currentUser} 
          onRoleSwitch={() => setCurrentUser(null)} 
        />
        <main className="flex-1 overflow-y-auto p-4 md:p-8">
          <div className="max-w-6xl mx-auto">
            {renderContent()}
          </div>
        </main>
      </div>
    </div>
  );
};

export default App;
